import type { Root } from './prototypeTypes';
import { articleMarkup } from './markup/articleMarkup';
import { topicMarkup } from './markup/topicMarkup';
import { aiCreateMarkup } from './markup/aiCreateMarkup';
import { browseMarkup } from './markup/browseMarkup';
import { communityMarkup } from './markup/communityMarkup';
import { feedbackMarkup } from './markup/feedbackMarkup';
import { githubTrendingMarkup } from './markup/githubTrendingMarkup';
import { historyMarkup } from './markup/historyMarkup';
import { homeMarkup } from './markup/homeMarkup';
import { ideaDetailMarkup } from './markup/ideaDetailMarkup';
import { membershipBillingMarkup } from './markup/membershipBillingMarkup';
import { messagesPageMarkup } from './markup/messagesPageMarkup';
import { pricingMarkup } from './markup/pricingMarkup';
import { profileViewMarkup } from './markup/profileMarkup';
import { redeemMarkup } from './markup/redeemMarkup';
import { referralCenterMarkup } from './markup/referralCenterMarkup';
import { socialMarkup } from './markup/socialMarkup';
import { aiJobDetailMarkup } from './markup/aiJobDetailMarkup';
import { hydrateCategoryButtons } from './categoryButtonsHydrator';
import { createBrowseHistoryEntryId } from './browsePaginationSessionStore';
import { DEFAULT_BROWSE_SORT } from './browseData';

/**
 * Direct Topic/Chapter SSR documents initially contain one course view. When a
 * user crosses between those two views, add the missing trusted SPA shell to
 * the existing app-main so the shared top navigation and mobile dock keep the
 * same DOM identity. Unrelated missing views still use document navigation.
 */
export function navigateWhenSsrViewMissing(
	root: Root,
	view: string,
	path: string,
	options: { allowBrowseTopicMaterialization?: boolean; preserveBrowseSnapshot?: boolean } = {},
): boolean {
	if (root.querySelector(`#view-${view}`) || !root.querySelector('#page-model')) return false;
	if (options.preserveBrowseSnapshot !== false) rememberBrowseSnapshotBeforeDocumentNavigation(root);
	if (materializeCoursePeerView(root, view)) return false;
	if (materializeBrowseTopicView(root, view, options)) return false;
	// Direct Topic/Chapter SSR keeps the initial document small and therefore
	// does not include the ordinary SPA views. Those views are still valid
	// in-app destinations: add the trusted fragment in place so clicking the
	// top/bottom navigation does not destroy a currently playing audio session.
	if (materializeStandardView(root, view)) return false;
	if (typeof window !== 'undefined') window.location.assign(path);
	return true;
}

const standardViewMarkups: Record<string, string> = {
	home: homeMarkup,
	'home-browse': browseMarkup,
	community: `<div class="view" id="view-community">${communityMarkup}</div>`,
	'github-trending': `<div class="view" id="view-github-trending">${githubTrendingMarkup}</div>`,
	social: socialMarkup,
	'ai-create': aiCreateMarkup,
	profile: profileViewMarkup,
	history: historyMarkup,
	feedback: feedbackMarkup,
	'idea-detail': ideaDetailMarkup,
	'ai-job-detail': aiJobDetailMarkup,
	pricing: pricingMarkup,
	'membership-billing': membershipBillingMarkup,
	referrals: referralCenterMarkup,
	messages: messagesPageMarkup,
	redeem: redeemMarkup,
};

function materializeStandardView(root: Root, view: string): boolean {
	const markup = standardViewMarkups[view];
	if (!markup || !appendTrustedViewMarkup(root, markup)) return false;
	// Direct Topic/Chapter SSR starts without Browse in the DOM. Runtime
	// bootstrap may already have the category catalog when Browse is later
	// materialized, so apply the idempotent catalog projection to the new view.
	if (view === 'home-browse') hydrateCategoryButtons(root);
	return true;
}

/**
 * The lightweight SPA shell intentionally document-navigates to a missing
 * course view. Preserve Browse intent on that history entry first, otherwise
 * browser Back can only rebuild an empty default catalog after the new
 * document mounts.
 */
function rememberBrowseSnapshotBeforeDocumentNavigation(root: Root): void {
	if (
		typeof window === 'undefined'
		|| !root.querySelector('#view-home-browse.active')
	) return;
	const input = root.querySelector<HTMLInputElement>('#browseSearchInput');
	const browseState = root as unknown as { __browseFilter?: string; __browseSort?: 'default' | 'trending' | 'newest' };
	const filter = browseState.__browseFilter ?? 'all';
	const currentState = window.history.state && typeof window.history.state === 'object' ? window.history.state : {};
	const browseHistoryEntryId = typeof currentState.browseHistoryEntryId === 'string' && currentState.browseHistoryEntryId.length > 0
		? currentState.browseHistoryEntryId
		: createBrowseHistoryEntryId();
	window.history.replaceState({
		...currentState,
		browseHistoryEntryId,
		browseSnapshot: { entryId: browseHistoryEntryId, filter, sort: browseState.__browseSort ?? DEFAULT_BROWSE_SORT, search: input?.value.trim() ?? '', scrollY: Math.max(0, window.scrollY) },
	}, '', window.location.href);
}

function materializeCoursePeerView(root: Root, view: string): boolean {
	const markup = view === 'article' && root.querySelector('#view-topic')
		? articleMarkup
		: view === 'topic' && root.querySelector('#view-article')
			? topicMarkup
			: null;
	return markup ? appendTrustedViewMarkup(root, markup) : false;
}

function appendTrustedViewMarkup(root: Root, markup: string): boolean {
	const main = root.querySelector<HTMLElement>('.app-main');
	if (!main) return false;

	const ownerDocument = root.ownerDocument ?? document;
	const template = ownerDocument.createElement('template');
	template.innerHTML = markup.trim();
	const peerView = template.content.firstElementChild;
	if (!peerView) return false;
	main.appendChild(peerView);
	return true;
}

function materializeBrowseTopicView(
	root: Root,
	view: string,
	options: { allowBrowseTopicMaterialization?: boolean },
): boolean {
	if (
		!options.allowBrowseTopicMaterialization
		|| view !== 'topic'
		|| !root.querySelector('#view-home-browse.active #browseGrid')
	) return false;
	return appendTrustedViewMarkup(root, topicMarkup);
}
